import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../context/LanguageContext';
import {
  EXTERNAL_ACCOUNT_LINK_UPDATED_EVENT,
  fetchExternalAccountLink,
} from '../../../services/externalAccountLinksApi';
import {
  getCurrentPlatformName,
  getPlatformNameFromUrl,
} from '../../../utils/platformIdentity';

const formatLinkDate = (value, locale, fallback) => {
  if (!value) {
    return fallback;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return date.toLocaleString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ConnectedAccounts = ({ user }) => {
  const { t, language } = useLanguage();
  const [linkState, setLinkState] = useState(null);
  const locale = language === 'en' ? 'en-US' : 'tr-TR';
  const currentPlatformName = getCurrentPlatformName('Agro-Mart');
  const agrochainDomain =
    linkState?.link?.externalDomain
    || linkState?.link?.metadata?.sourceOrigin
    || '';
  const linkStatus = `${linkState?.link?.status || ''}`.trim().toLowerCase();
  const connectedPlatformName = getPlatformNameFromUrl(
    agrochainDomain,
    t('Agrochain', 'Agrochain'),
  );

  useEffect(() => {
    let isMounted = true;

    const loadLinkState = async () => {
      try {
        const response = await fetchExternalAccountLink('AGROCHAIN');
        if (isMounted) {
          setLinkState(response || null);
        }
      } catch (_error) {
        if (isMounted) {
          setLinkState(null);
        }
      }
    };

    loadLinkState();

    const handleLinkUpdate = (event) => {
      const system = `${event?.detail?.system || ''}`.toUpperCase();
      if (system === 'AGROCHAIN') {
        setLinkState(event?.detail?.response || null);
      }
    };

    window.addEventListener(EXTERNAL_ACCOUNT_LINK_UPDATED_EVENT, handleLinkUpdate);

    return () => {
      isMounted = false;
      window.removeEventListener(EXTERNAL_ACCOUNT_LINK_UPDATED_EVENT, handleLinkUpdate);
    };
  }, []);

  const connectionState = useMemo(() => {
    if (linkStatus === 'linked') {
      return {
        label: t('Connection Active', 'Bağlantı Aktif'),
        toneClass: 'bg-success-100 text-success',
        dotClass: 'bg-success',
        description: t(
          `Your ${connectedPlatformName} account is actively linked to this ${currentPlatformName} account.`,
          `${connectedPlatformName} hesabınız bu ${currentPlatformName} hesabına aktif olarak bağlı.`,
        ),
      };
    }

    if (linkStatus === 'pending') {
      return {
        label: t('Connection Pending', 'Bağlantı Bekleniyor'),
        toneClass: 'bg-warning-100 text-warning',
        dotClass: 'bg-warning',
        description: t(
          `The ${connectedPlatformName} linking flow has started but has not been completed yet.`,
          `${connectedPlatformName} bağlama akışı başlatıldı ancak henüz tamamlanmadı.`,
        ),
      };
    }

    if (linkState?.link) {
      return {
        label: t('Connection Inactive', 'Bağlantı Aktif Değil'),
        toneClass: 'bg-error-100 text-error',
        dotClass: 'bg-error',
        description: t(
          `The previous ${connectedPlatformName} link is no longer active.`,
          `Önceki ${connectedPlatformName} bağlantısı artık aktif değil.`,
        ),
      };
    }

    return {
      label: t('Not Connected', 'Bağlı Değil'),
      toneClass: 'bg-secondary-100 text-text-secondary',
      dotClass: 'bg-text-secondary',
      description: t(
        `No ${connectedPlatformName} account is linked to this ${currentPlatformName} account yet.`,
        `Bu ${currentPlatformName} hesabına henüz bir ${connectedPlatformName} hesabı bağlanmadı.`,
      ),
    };
  }, [connectedPlatformName, currentPlatformName, linkState?.link, linkStatus, t]);

  const missingBadge = (
    <span className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary-100 px-3 py-1 text-xs font-semibold text-text-secondary">
      <span className="h-2 w-2 rounded-full bg-text-secondary"></span>
      {t('Not available yet', 'Henüz yok')}
    </span>
  );

  const infoItems = [
    {
      label: t('Linked Platform', 'Bağlı Platform'),
      value: connectedPlatformName,
      icon: 'Link2',
    },
    {
      label: t('Linked Address', 'Bağlı Adres'),
      value: agrochainDomain || missingBadge,
      icon: 'Globe',
    },
    {
      label: t(`${connectedPlatformName} Account`, `${connectedPlatformName} Hesabı`),
      value:
        linkState?.link?.externalEmail
        || linkState?.link?.externalDisplayName
        || missingBadge,
      icon: 'User',
    },
    {
      label: t(`${currentPlatformName} Account`, `${currentPlatformName} Hesabı`),
      value: user?.email || missingBadge,
      icon: 'Mail',
    },
    {
      label: t('Connection Status', 'Bağlantı Durumu'),
      value: connectionState.label,
      icon: 'ShieldCheck',
    },
    {
      label: t('Last Verification', 'Son Doğrulama'),
      value: formatLinkDate(
        linkState?.link?.lastVerifiedAt || linkState?.link?.updatedAt,
        locale,
        missingBadge,
      ),
      icon: 'Clock3',
    },
  ];

  return (
    <div className="bg-surface rounded-lg shadow-elevation-1">
      <div className="px-6 py-4 border-b border-border">
        <h2 className="text-xl font-semibold text-text-primary font-heading">
          {t('Connection Details', 'Bağlantı Bilgileri')}
        </h2>
        <p className="text-sm text-text-secondary mt-1">
          {t(
            `Track the active ${connectedPlatformName} connection tied to this marketplace account here.`,
            `Bu pazaryeri hesabına bağlı aktif ${connectedPlatformName} bağlantısını burada takip edin.`,
          )}
        </p>
      </div>

      <div className="p-6 space-y-6">
        <div className="rounded-lg border border-border bg-background px-4 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary">
                <Icon name="Link2" size={20} />
              </div>
              <div>
                <h3 className="text-base font-semibold text-text-primary">
                  {t(`${connectedPlatformName} Connection`, `${connectedPlatformName} Bağlantısı`)}
                </h3>
                <p className="text-sm text-text-secondary mt-1">
                  {connectionState.description}
                </p>
              </div>
            </div>

            <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${connectionState.toneClass}`}>
              <span className={`h-2 w-2 rounded-full ${connectionState.dotClass}`}></span>
              {connectionState.label}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {infoItems.map((item) => (
            <div key={item.label} className="rounded-lg border border-border bg-background px-4 py-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-text-secondary">
                  <Icon name={item.icon} size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-text-secondary">{item.label}</p>
                  <div className="mt-1 text-sm font-semibold text-text-primary break-words">
                    {item.value}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ConnectedAccounts;
